import { Middleware, Dispatch } from 'redux';
import { PayloadMetaAction, TypeConstant } from 'typesafe-actions';
import _ from 'lodash';
import { DataState } from '../state';
import { RootAction } from '../actions';
import { stableSerializeActionPayload } from './stableSerializeActionPayload';

/** Debounce timers keyed by action type + stable payload (not type alone — same-type actions with different payloads no longer cancel each other). */
const debounced: Record<string, ReturnType<typeof setTimeout>> = {};

function isDebouncedAction<T extends TypeConstant, P, M extends { debounce: number }>(
  action: RootAction | PayloadMetaAction<T, P, M>
): action is PayloadMetaAction<T, P, M> {
  const debounce = _.get(action, 'meta.debounce', undefined);
  if (_.isNil(debounce)) {
    return false;
  }
  return true;
}

export const debounceMiddleware: Middleware<Dispatch<RootAction>, DataState, Dispatch<RootAction>> = () => (
  next: Dispatch<RootAction>
) => (action: RootAction) => {
  if (isDebouncedAction(action)) {
    const time = action.meta && action.meta.debounce;
    if (!time) {
      return next(action);
    } else {
      const payload = 'payload' in action ? (action as { payload?: unknown }).payload : undefined;
      const debounceKey = `${action.type}:${stableSerializeActionPayload(payload)}`;
      const existing = debounced[debounceKey];
      if (existing) {
        clearTimeout(existing);
      }
      debounced[debounceKey] = setTimeout(() => {
        next(action);
      }, time);
    }
  } else {
    next(action);
  }
};
